import { StandardEvents } from '@shopify/events';

/**
 * Keeps an Aqualumen product block in step with the selected variant.
 * After a variant change, swaps its content for the matching element in the
 * section HTML the variant picker already fetched (same approach as product-price).
 */
class AqVariantSync extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`aq-variant-sync[data-sync-id="${this.dataset.syncId}"]`);
        if (!fresh) return;
        this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

/* Romanian public holidays that fall on fixed dates (MM-DD). Orthodox Easter and Pentecost move every year. */
const FIXED_HOLIDAYS = ['01-01', '01-02', '01-06', '01-07', '01-24', '05-01', '06-01', '08-15', '11-30', '12-01', '12-25', '12-26'];

/**
 * Shows the delivery window as real dates ("vineri, 30 octombrie"), skipping weekends and
 * fixed public holidays. Computed in the browser so cached pages never show stale dates.
 * Without JavaScript the server-rendered "2-5 zile lucrătoare" text stays.
 */
class AqDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const min = Number(this.dataset.min);
    const max = Number(this.dataset.max);
    const target = this.querySelector('[data-estimate]');
    if (!target || !Number.isFinite(min) || !Number.isFinite(max) || max < min) return;

    const format = new Intl.DateTimeFormat('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' });
    const from = format.format(this.#addBusinessDays(new Date(), min));
    const to = format.format(this.#addBusinessDays(new Date(), max));
    target.textContent = from === to ? from : `între ${from} și ${to}`;
  }

  /** @param {Date} date @param {number} days */
  #addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      if (this.#isBusinessDay(result)) added += 1;
    }
    return result;
  }

  /** @param {Date} date */
  #isBusinessDay(date) {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;
    const key = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return !FIXED_HOLIDAYS.includes(key);
  }
}

/**
 * The "01 / 06" counter and progress line on the product gallery. Follows the slideshow's
 * select events (arrows, thumbnails, swipe and scroll), so it never drives the slides itself.
 */
class AqGalleryCounter extends HTMLElement {
  /** @type {Element | null} */
  #slideshow = null;

  connectedCallback() {
    this.#slideshow = this.closest('slideshow-component');
    this.#slideshow?.addEventListener('slideshow:select', this.#handleSelect);
    const current = Number(/** @type {any} */ (this.#slideshow)?.current);
    this.#render(Number.isFinite(current) ? current : 0);
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#slideshow = null;
  }

  /** @param {Event & { detail?: { index?: number } }} event */
  #handleSelect = (event) => {
    if (event.target !== this.#slideshow) return;
    const index = event.detail?.index;
    if (typeof index === 'number') this.#render(index);
  };

  /** @param {number} index */
  #render(index) {
    const count = Math.max(Number(this.dataset.count) || 1, 1);
    const position = Math.min(Math.max(index, 0), count - 1) + 1;
    const target = this.querySelector('[data-current]');
    if (target) target.textContent = String(position).padStart(2, '0');
    this.style.setProperty('--aq-progress', String(position / count));
  }
}

/**
 * Collapses a long block (the description, the specification table) to a preview with a fade and a
 * "show more" button, like a dropdown that opens halfway. The content stays whole in the HTML, so
 * without JavaScript, or when it is short enough already, nothing is hidden and there is no button.
 */
class AqExpandable extends HTMLElement {
  /** @type {HTMLElement | null} */
  #body = null;
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#body = this.querySelector('[data-expand-body]');
    if (!this.#body) return;
    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.#body);
    requestAnimationFrame(() => this.#measure());
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#button?.remove();
    this.#button = null;
  }

  get #preview() {
    return Number.parseFloat(getComputedStyle(this).getPropertyValue('--aq-expand-height')) || 0;
  }

  #measure() {
    const body = this.#body;
    if (!body || this.classList.contains('is-open')) return;
    const needsButton = body.scrollHeight > this.#preview * 1.25;
    this.classList.toggle('is-collapsed', needsButton);
    if (needsButton && !this.#button) this.#addButton();
    if (!needsButton) {
      this.#button?.remove();
      this.#button = null;
    }
  }

  #addButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'aq-expand__toggle';
    button.setAttribute('aria-expanded', 'false');
    button.textContent = this.dataset.more || '';
    button.addEventListener('click', () => this.#toggle());
    this.append(button);
    this.#button = button;
  }

  #toggle() {
    const open = !this.classList.contains('is-open');
    this.classList.toggle('is-open', open);
    this.classList.toggle('is-collapsed', !open);
    if (!this.#button) return;
    this.#button.setAttribute('aria-expanded', String(open));
    this.#button.textContent = (open ? this.dataset.less : this.dataset.more) || '';
    if (!open) this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

if (!customElements.get('aq-variant-sync')) customElements.define('aq-variant-sync', AqVariantSync);
if (!customElements.get('aq-gallery-counter')) customElements.define('aq-gallery-counter', AqGalleryCounter);
if (!customElements.get('aq-delivery-estimate')) customElements.define('aq-delivery-estimate', AqDeliveryEstimate);
if (!customElements.get('aq-expandable')) customElements.define('aq-expandable', AqExpandable);
